import type { PlanType } from '@labrav/react-components'

import type { links } from '../productsListing/types'
import type { AzureOfferPlan } from '../privateOffer/types'
import type {
  AgreementDurationType,
  AzurePricingType,
  ChannelType,
  ContractDurationType,
  Dimension,
  DiscountType,
  EulaType,
  FailedErrorMessage,
  Installments,
  MetaData,
  OfferEndBuyers,
  OfferRecipients,
  PriceTypes,
  ResellerAgreementStatus,
  Stakeholders,
} from '../../types/flyout'
import type {
  AgreementApprovers,
  CPPOProducts,
  MicrosoftResellerDetails,
  ResellerInfo,
} from '../../types/resaleAuthorization'

export enum ReceivedResellerAuthorizationActionTypes {
  SET_RECEIVED_RESALE_AUTHORIZATIONS_DATA = 'SET_RECEIVED_RESALE_AUTHORIZATIONS_DATA',
  SET_CURRENT_RECEIVED_RESELLER_AUTHORIZATION = 'SET_CURRENT_RECEIVED_RESELLER_AUTHORIZATION',
  CLEAR_CURRENT_RECEIVED_RESELLER_AUTHORIZATION = 'CLEAR_CURRENT_RECEIVED_RESELLER_AUTHORIZATION',
  SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA = 'SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA',
  SET_SELECTED_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA = 'SET_SELECTED_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA',
  CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION = 'CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION',
  CLEAR_RECEIVED_RESALE_AUTHORIZATIONS_DATA = 'CLEAR_RECEIVED_RESALE_AUTHORIZATIONS_DATA',
}

export type ResaleAuthorizationType = 'CREATED' | 'RECEIVED'

export interface ReceivedResellerAuthorization {
  id: string
  isvName?: string
  isvCloudAccountId?: string
  crmAwsResellerAgreementObjectId: string
  agreementDescription: string
  agreementName: string
  buyerAwsAccountId: string
  agreementStartDate?: string
  agreementExpirationDate: string
  emailCustomMessage: string
  contractDurationInMonthsForEndCustomer?: number
  agreementDurationType: AgreementDurationType
  discountType: DiscountType
  contractDurationType: ContractDurationType
  endCustomerEulaType: EulaType
  endCustomerEulaFiles: string[]
  resellerLicenseAgreementType: EulaType
  resellerLicenseAgreementFiles: string[]
  percentageDiscount?: number
  awsAgreementId: string
  agreementApprovers: AgreementApprovers[]
  agreementRecipients: OfferRecipients[]
  customOfferStakeholders?: Stakeholders[]
  agreementStakeholders: Stakeholders[]
  installmentInfo: Installments[]
  metaData: MetaData[]
  renewal?: boolean
  renewalChannel?: ChannelType | null
  createdAt: string
  status: ResellerAgreementStatus
  resellerName: string
  draft?: boolean
  pricesArray?: [keyof PriceTypes]
  dimensions?: Dimension[]
  companyName?: string
  agreementOrigin?: ResaleAuthorizationType
  offerExtendedStatus?: string
  buyerName?: string
  serviceStartDate?: string
  productName?: string
  productType?: PlanType
  updatedAt?: string
  error?: string
  productId?: string
  receivedOn?: string
  auditJson?: ResaleAuthAuditJson
  cloudProductName?: string
  mppoDescription?: string
  offerRecipients?: OfferRecipients[]
  offerStakeholders: Stakeholders[]
  resellerDetails?: MicrosoftResellerDetails[]
  azurePricingType?: AzurePricingType
  plans?: AzureOfferPlan[]
  beneficiaryId?: string
  variableStartDate?: boolean
  existingContractRenewal?: boolean
  offerStartDate?: string
  acceptBy?: string
  mppoName?: string
  eulaType?: EulaType
  eulaFiles?: string[]
  offerExpirationDate?: string
  sellerId?: string
  labraOfferUrl?: string
  acceptanceLink?: string
  azureMppoId?: string
  offerStatus?: ResellerAgreementStatus
  statusLogs?: MPPOResaleAuthAuditJson
  errorDetails?: {
    errors?: FailedErrorMessage[]
  }
  offerEndBuyers?: OfferEndBuyers[]
  currency?: string
  netPaymentTerms?: string
}

export type { FailedErrorMessage }

export interface ResaleAuthStages {
  'Authorization created'?: string | null
  'Authorization activated'?: string | null
  'Authorization duration ended'?: string | null
  'Authorization deactivated'?: string | null
  'Authorization completed'?: string | null
  'Channel offer accepted'?: string | null
  'Authorization creation failed'?: string | null
}

export enum ResaleAuthStageToStatusRelationMapEnum {
  CREATED = 'Authorization created',
  ACTIVE = 'Authorization activated',
  EXPIRED = 'Authorization duration ended',
  COMPLETED = 'Authorization completed',
  CHANNEL_OFFER_ACCEPTED = 'Channel offer accepted',
  DEACTIVATED = 'Authorization deactivated',
  FAILED = 'Authorization creation failed',
}

export interface MPPOResaleAuthStages {
  'Created at Labra'?: string | null
  'Sent to reseller'?: string | null
  'Viewed by reseller'?: string | null
  'Extended to buyer'?: string | null
  'Pending purchase'?: string | null
  'Pending account configuration'?: string | null
  Accepted?: string | null
  Expired?: string | null
  'Deactivation in progress'?: string | null
  Deactivated?: string | null
}

export enum MPPOResaleAuthStageToStatusRelationMapEnum {
  'offerInDraft' = 'Created at Labra',
  'offerCreationInProgress' = 'Created at Labra',
  'offerCreationFailed' = 'Sent to reseller',
  'offerSentReseller' = 'Sent to reseller',
  'offerViewedReseller' = 'Viewed by reseller',
  'offerExtendedToBuyer' = 'Extended to buyer',
  'offerTermsAcceptedBuyer' = 'Pending purchase',
  'offerPurchaseComplete' = 'Pending account configuration',
  'offerAccepted' = 'Accepted',
  'offerExpired' = 'Expired',
  'offerRestrictionInProgress' = 'Deactivation in progress',
  'offerRestricted' = 'Deactivated',
}

export type ResaleAuthAuditJson = {
  resaleAuthCreatedAt: string | null
  resaleAuthCreationFailedAt: string | null
  resaleAuthActivatedAt: string | null
  resaleAuthDeactivatedAt: string | null
  resaleAuthExpiredAt: string | null
  resaleAuthCompletedAt: string | null
  resaleAuthChannelOfferAcceptedAt?: string | null
  channelOfferAcceptedAt?: string | null
}

export type MPPOResaleAuthAuditJson = {
  offerInDraft: string | null
  offerCreationInProgress: string | null
  offerCreationFailed: string | null
  offerSentReseller: string | null
  offerViewedReseller: string | null
  offerExtendedToBuyer: string | null
  offerTermsAcceptedBuyer: string | null
  offerPurchaseComplete: string | null
  offerAccepted: string | null
  offerExpired: string | null
  offerRestrictionInProgress: string | null
  offerRestricted: string | null
  offerAcceptedAt: string | null
  offerExpiredAt: string | null
  offerRestrictionInProgressAt: string | null
  offerRestrictedAt: string | null
}

export type ViewResellerAuthorizationType = ReceivedResellerAuthorization & {
  resellersInfo?: ResellerInfo[]
  products?: CPPOProducts[]
  awsProductId?: string
  serviceStartDate?: string
}

export type ReceivedResellerAuthorizationApiResponse = {
  pageNumber?: number
  pageSize?: number
  totalCount?: number
  sortBy?: string
  sortOrder?: string
  links?: links
}

export type ApiResponse = ReceivedResellerAuthorizationApiResponse

export type ReceivedResellerAuthorizationState = {
  resellerAgreements: ReceivedResellerAuthorization[]
  count: number
  pageNumber: number
  noOfPage: number | null
  pageSize: number
  currentReceivedResellerAuthorizations: {
    [x: string]: ReceivedResellerAuthorization
  }
  activeReceivedResellerAuthorizations: ReceivedResellerAuthorization[]
  selectedActiveReceivedResellerAuthorization: string
} & ReceivedResellerAuthorizationApiResponse

export const initialReceivedResellerAuthorizationState: ReceivedResellerAuthorizationState =
  {
    resellerAgreements: [],
    count: 0,
    pageNumber: 0,
    noOfPage: null,
    pageSize: 10,
    currentReceivedResellerAuthorizations: {},
    activeReceivedResellerAuthorizations: [],
    selectedActiveReceivedResellerAuthorization: '',
  }

export type ReceivedResellerAuthorizationAction =
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_RECEIVED_RESALE_AUTHORIZATIONS_DATA
      payload: {
        agreements: {
          resellerAgreements: ReceivedResellerAuthorization[]
          count: number
        }
        pageNumber: number
        pageSize: number
      }
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_CURRENT_RECEIVED_RESELLER_AUTHORIZATION
      payload: ReceivedResellerAuthorization
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.CLEAR_CURRENT_RECEIVED_RESELLER_AUTHORIZATION
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA
      payload: {
        agreements: ReceivedResellerAuthorization[]
      }
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.SET_SELECTED_ACTIVE_RECEIVED_RESALE_AUTHORIZATIONS_DATA
      payload: {
        authorizationId: string
      }
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.CLEAR_ACTIVE_RECEIVED_RESELLER_AUTHORIZATION
    }
  | {
      type: ReceivedResellerAuthorizationActionTypes.CLEAR_RECEIVED_RESALE_AUTHORIZATIONS_DATA
    }

export type ProcessReceivedResellerAgreementsForCPParams = {
  partnerId: string
  pageSize?: number
  pageNumber?: number
  productId?: string
  crmOpportunityId?: string
  status?: string
  origin?: string
  input?: string
  selectedInput?: string
  sectionName?: string
  cloudProductUid?: string
}

export type GetReceivedResaleAuthorizationsForCPParams = {
  partnerId: string
  pageSize?: number
  pageNumber?: number
  crmOpportunityId?: string
  input?: string
  selectedInput?: string
  sectionName?: string
}

export type GetActiveReceivedResaleAuthorizationsParams = {
  partnerId: string
  crmOpportunityId?: string
  subscriptionId?: string
}

export type { ResellerInfo, CPPOProducts }
